/**
 * CalendarResourcesPage
 *
 * CRUD page for managing Calendar Resources (Bookable Resources) per agent.
 * Allows operators to create, edit, delete resources with custom attributes,
 * filter by resource type, active status, and search by name.
 * Includes auto-generation of resources from agent context via LLM.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 5.3, 5.4, 7.1, 7.2, 7.4, 7.5, 7.6
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Badge, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { showGrowl } from '../../ui/growlState';
import { AutoGenerateButton } from './calendar/AutoGenerateButton';
import type { ExtractedResource } from './calendar/AutoGenerateButton';
import { GenerationPreviewModal } from './calendar/GenerationPreviewModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ResourceType {
  id: string;
  name: string;
  bookingMode: 'daily' | 'time-block' | 'appointment';
}

interface ResourceMediaItem {
  id: string;
  kind: 'image' | 'video';
  mimeType: string;
  originalName: string;
  storageKey: string;
  sizeBytes: number;
  sortOrder: number;
}

interface CalendarResource {
  id: string;
  virtualAgentId: string;
  resourceTypeId: string;
  resourceType: ResourceType;
  name: string;
  description: string | null;
  color: string | null;
  capacity: number;
  customAttributes: Record<string, string> | null;
  mediaAttachments?: ResourceMediaItem[] | null;
  isActive: boolean;
  icalImportUrl?: string | null;
  icalSyncEnabled?: boolean;
  icalLastSyncedAt?: string | null;
  icalLastSyncError?: string | null;
  createdAt: string;
  updatedAt: string;
}

function parseResourceMedia(raw: unknown): ResourceMediaItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  const out: ResourceMediaItem[] = [];
  for (let index = 0; index < raw.length; index++) {
    const row = raw[index];
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    if (typeof o.id !== 'string' || (o.kind !== 'image' && o.kind !== 'video')) continue;
    if (typeof o.mimeType !== 'string' || typeof o.storageKey !== 'string') continue;
    const sortOrder =
      typeof o.sortOrder === 'number' && Number.isFinite(o.sortOrder) ? Math.floor(o.sortOrder) : index;
    out.push({
      id: o.id,
      kind: o.kind,
      mimeType: o.mimeType,
      originalName: typeof o.originalName === 'string' ? o.originalName : '',
      storageKey: o.storageKey,
      sizeBytes: typeof o.sizeBytes === 'number' ? o.sizeBytes : 0,
      sortOrder,
    });
  }
  return out.sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
}

function reorderMediaIds(ids: string[], dragId: string, dropId: string): string[] {
  const from = ids.indexOf(dragId);
  const to = ids.indexOf(dropId);
  if (from < 0 || to < 0 || from === to) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, dragId);
  return next;
}

function IconGrip() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}

const MEDIA_ZOOM_PANEL_WIDTH = 420;
const MEDIA_ZOOM_PANEL_MAX_HEIGHT = 380;

type MediaZoomAnchor = { id: string; top: number; left: number };

function ResourceMediaZoomPopover({
  anchor,
  url,
  label,
  onKeepOpen,
  onScheduleClose,
}: {
  anchor: MediaZoomAnchor;
  url: string;
  label: string;
  onKeepOpen: () => void;
  onScheduleClose: () => void;
}) {
  const left = Math.min(
    Math.max(12, anchor.left),
    window.innerWidth - MEDIA_ZOOM_PANEL_WIDTH - 12
  );
  const top = Math.min(
    Math.max(12, anchor.top),
    window.innerHeight - MEDIA_ZOOM_PANEL_MAX_HEIGHT - 12
  );

  return createPortal(
    <div
      role="dialog"
      aria-label={label ? `Vista ampliada: ${label}` : 'Vista ampliada'}
      onMouseEnter={onKeepOpen}
      onMouseLeave={onScheduleClose}
      style={{
        position: 'fixed',
        top,
        left,
        zIndex: 2100,
        width: MEDIA_ZOOM_PANEL_WIDTH,
        maxWidth: `calc(100vw - 24px)`,
        padding: tokens.spacing[3],
        borderRadius: tokens.borderRadius.xl,
        backgroundColor: tokens.colors.neutral[50],
        boxShadow: tokens.shadows.xl,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        pointerEvents: 'auto',
        animation: 'mediaZoomIn 140ms ease-out',
      }}
    >
      <img
        src={url}
        alt={label || 'Vista ampliada'}
        style={{
          display: 'block',
          width: '100%',
          maxHeight: MEDIA_ZOOM_PANEL_MAX_HEIGHT - 56,
          objectFit: 'contain',
          borderRadius: tokens.borderRadius.md,
          backgroundColor: tokens.colors.neutral[100],
        }}
      />
      {label ?
        <p
          style={{
            margin: `${tokens.spacing[2]} 0 0`,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[600],
            lineHeight: tokens.typography.lineHeight.normal,
            wordBreak: 'break-word',
          }}
        >
          {label}
        </p>
      : null}
    </div>,
    document.body
  );
}

interface FormData {
  name: string;
  resourceTypeId: string;
  description: string;
  color: string;
  capacity: number;
  isActive: boolean;
  customAttributes: { key: string; value: string }[];
  icalImportUrl: string;
  icalSyncEnabled: boolean;
}

const EMPTY_FORM: FormData = {
  name: '',
  resourceTypeId: '',
  description: '',
  color: '#3B82F6',
  capacity: 1,
  isActive: true,
  customAttributes: [],
  icalImportUrl: '',
  icalSyncEnabled: false,
};

const RESOURCE_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconBox() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="22.08" x2="12" y2="12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" strokeLinecap="round" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CalendarResourcesPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [resources, setResources] = useState<CalendarResource[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterActive, setFilterActive] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [syncingResourceId, setSyncingResourceId] = useState<string | null>(null);
  const [syncingKind, setSyncingKind] = useState<'ical' | null>(null);

  // Auto-generation state
  const [generatedResources, setGeneratedResources] = useState<ExtractedResource[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hasContext, setHasContext] = useState(false);

  /** Medios del recurso en el modal de edición (persistidos vía API al subir/borrar). */
  const [mediaList, setMediaList] = useState<ResourceMediaItem[]>([]);
  const [mediaBlobUrls, setMediaBlobUrls] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<{ fileName: string; percent: number } | null>(null);
  const [dragOverMedia, setDragOverMedia] = useState(false);
  const [reorderingMedia, setReorderingMedia] = useState(false);
  const [mediaDragId, setMediaDragId] = useState<string | null>(null);
  const [mediaDropTargetId, setMediaDropTargetId] = useState<string | null>(null);
  const [mediaPreviewErrorIds, setMediaPreviewErrorIds] = useState<Set<string>>(() => new Set());
  const [mediaZoomAnchor, setMediaZoomAnchor] = useState<MediaZoomAnchor | null>(null);
  const mediaZoomCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const mediaBlobUrlsRef = useRef<Record<string, string>>({});

  const mediaIdsKey = useMemo(() => mediaList.map((m) => m.id).join('|'), [mediaList]);

  const cancelMediaZoomClose = useCallback(() => {
    if (mediaZoomCloseTimerRef.current) {
      clearTimeout(mediaZoomCloseTimerRef.current);
      mediaZoomCloseTimerRef.current = null;
    }
  }, []);

  const scheduleMediaZoomClose = useCallback(() => {
    cancelMediaZoomClose();
    mediaZoomCloseTimerRef.current = setTimeout(() => {
      setMediaZoomAnchor(null);
      mediaZoomCloseTimerRef.current = null;
    }, 160);
  }, [cancelMediaZoomClose]);

  const openMediaZoom = useCallback(
    (id: string, el: HTMLElement) => {
      cancelMediaZoomClose();
      const rect = el.getBoundingClientRect();
      const preferLeft = rect.right + 10;
      const fitsRight = preferLeft + MEDIA_ZOOM_PANEL_WIDTH <= window.innerWidth - 12;
      setMediaZoomAnchor({
        id,
        top: Math.max(12, rect.top + rect.height / 2 - 80),
        left: fitsRight ? preferLeft : Math.max(12, rect.left - MEDIA_ZOOM_PANEL_WIDTH - 10),
      });
    },
    [cancelMediaZoomClose]
  );

  useEffect(() => {
    return () => {
      if (mediaZoomCloseTimerRef.current) clearTimeout(mediaZoomCloseTimerRef.current);
    };
  }, []);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadResourceTypes = useCallback(async () => {
    if (!agentId || !token) return;
    try {
      const data = await apiGet<ResourceType[]>(
        `/api/agents/${agentId}/calendar/resource-types`,
        token
      );
      setResourceTypes(data);
    } catch {
      // Non-critical: types dropdown won't populate
    }
  }, [agentId, token]);

  const loadResources = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (filterTypeId) params.set('resourceTypeId', filterTypeId);
      if (filterActive) params.set('isActive', filterActive);

      const qs = params.toString();
      const url = `/api/agents/${agentId}/calendar/resources${qs ? `?${qs}` : ''}`;
      const data = await apiGet<CalendarResource[]>(url, token);
      setResources(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, token, search, filterTypeId, filterActive]);

  useEffect(() => {
    void loadResourceTypes();
  }, [loadResourceTypes]);

  useEffect(() => {
    void loadResources();
  }, [loadResources]);

  // ─── Context Check (for auto-generate button) ───────────────────────────

  const checkAgentContext = useCallback(async () => {
    if (!agentId || !token) return;
    try {
      const [docs, urls] = await Promise.all([
        apiGet<{ id: string }[]>(`/api/console/agents/${agentId}/context-documents`, token),
        apiGet<{ id: string }[]>(`/api/console/agents/${agentId}/context-urls`, token),
      ]);
      setHasContext(docs.length > 0 || urls.length > 0);
    } catch {
      setHasContext(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void checkAgentContext();
  }, [checkAgentContext]);

  /** Previsualización autenticada (blob) de cada adjunto al editar. */
  useEffect(() => {
    mediaBlobUrlsRef.current = mediaBlobUrls;
  }, [mediaBlobUrls]);

  useEffect(() => {
    if (!modalOpen || !editingId || !token || !agentId) {
      setMediaBlobUrls((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return {};
      });
      mediaBlobUrlsRef.current = {};
      setMediaPreviewErrorIds(new Set());
      return;
    }

    const neededIds = new Set(mediaList.map((m) => m.id));
    setMediaPreviewErrorIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of prev) {
        if (!neededIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setMediaBlobUrls((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(next)) {
        if (!neededIds.has(id)) {
          URL.revokeObjectURL(next[id]!);
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    let cancelled = false;
    void (async () => {
      const pending = mediaList.filter((m) => !mediaBlobUrlsRef.current[m.id]);
      if (pending.length === 0) return;

      await Promise.all(
        pending.map(async (m) => {
          try {
            const r = await fetch(
              `/api/agents/${agentId}/calendar/resources/${editingId}/media/${m.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!r.ok || cancelled) {
              if (!cancelled) {
                setMediaPreviewErrorIds((prev) => new Set(prev).add(m.id));
              }
              return;
            }
            const contentType = r.headers.get('content-type') ?? '';
            if (contentType.includes('application/json')) {
              if (!cancelled) {
                setMediaPreviewErrorIds((prev) => new Set(prev).add(m.id));
              }
              return;
            }
            const rawBlob = await r.blob();
            if (cancelled || rawBlob.size === 0) {
              if (!cancelled) {
                setMediaPreviewErrorIds((prev) => new Set(prev).add(m.id));
              }
              return;
            }
            const blobType = rawBlob.type || m.mimeType || 'application/octet-stream';
            const blob =
              rawBlob.type && rawBlob.type !== 'application/octet-stream'
                ? rawBlob
                : new Blob([await rawBlob.arrayBuffer()], { type: blobType });
            const objectUrl = URL.createObjectURL(blob);
            setMediaBlobUrls((prev) => {
              if (prev[m.id]) {
                URL.revokeObjectURL(objectUrl);
                return prev;
              }
              const next = { ...prev, [m.id]: objectUrl };
              mediaBlobUrlsRef.current = next;
              return next;
            });
            setMediaPreviewErrorIds((prev) => {
              if (!prev.has(m.id)) return prev;
              const next = new Set(prev);
              next.delete(m.id);
              return next;
            });
          } catch {
            if (!cancelled) {
              setMediaPreviewErrorIds((prev) => new Set(prev).add(m.id));
            }
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [modalOpen, editingId, agentId, token, mediaIdsKey]);

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setMediaList([]);
    setModalOpen(true);
  };

  const openEditModal = (resource: CalendarResource) => {
    setEditingId(resource.id);
    const customAttributes = Object.entries(resource.customAttributes || {}).map(([key, value]) => ({ key, value }));
    setForm({
      name: resource.name,
      resourceTypeId: resource.resourceTypeId,
      description: resource.description || '',
      color: resource.color && RESOURCE_COLOR_RE.test(resource.color) ? resource.color.toUpperCase() : '#3B82F6',
      capacity: resource.capacity,
      isActive: resource.isActive,
      customAttributes,
      icalImportUrl: resource.icalImportUrl ?? '',
      icalSyncEnabled: resource.icalSyncEnabled === true,
    });
    setFormErrors({});
    setMediaList(parseResourceMedia(resource.mediaAttachments));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setMediaList([]);
    setUploadProgress(null);
    setDragOverMedia(false);
    cancelMediaZoomClose();
    setMediaZoomAnchor(null);
    setMediaDragId(null);
    setMediaDropTargetId(null);
    setMediaPreviewErrorIds(new Set());
  };

  const uploadOneFile = (file: File, resourceId: string): Promise<void> => {
    if (!agentId || !token) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress({
            fileName: file.name,
            percent: Math.round((100 * e.loaded) / e.total),
          });
        }
      });
      xhr.open('POST', `/api/agents/${agentId}/calendar/resources/${resourceId}/media`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = () => {
        setUploadProgress(null);
        let parsed: unknown;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          showGrowl('No se pudo leer la respuesta del servidor', 'error');
          reject(new Error('parse'));
          return;
        }
        const j = parsed as {
          success?: boolean;
          data?: { attachment?: ResourceMediaItem };
          error?: { message?: string };
        };
        if (xhr.status >= 200 && xhr.status < 300 && j.success && j.data?.attachment) {
          setMediaList((prev) => [...prev, j.data!.attachment!]);
          resolve();
          return;
        }
        showGrowl(j.error?.message || `Error al subir (${xhr.status})`, 'error');
        reject(new Error(j.error?.message || 'upload'));
      };
      xhr.onerror = () => {
        setUploadProgress(null);
        showGrowl('Error de red al subir el archivo', 'error');
        reject(new Error('network'));
      };
      xhr.send(form);
    });
  };

  const processMediaFiles = async (fileList: FileList | File[]) => {
    const files = [...fileList];
    const rid = editingId;
    if (!rid) {
      showGrowl('Creá el recurso con "Crear" primero; después podés arrastrar o elegir fotos y videos.', 'info');
      return;
    }
    for (const f of files) {
      if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) {
        showGrowl(`"${f.name}" no es imagen ni video`, 'error');
        continue;
      }
      try {
        await uploadOneFile(f, rid);
      } catch {
        /* continuar con el resto */
      }
    }
    await loadResources();
  };

  const saveMediaOrder = async (attachmentIds: string[], opts?: { silent?: boolean }) => {
    if (!editingId || !agentId || !token) return;
    setReorderingMedia(true);
    try {
      const data = await apiPut<{ mediaAttachments: unknown }>(
        `/api/agents/${agentId}/calendar/resources/${editingId}/media/order`,
        { attachmentIds },
        token,
        opts?.silent ? {} : { successMessage: 'Orden de fotos actualizado' }
      );
      setMediaList(parseResourceMedia(data.mediaAttachments));
      await loadResources();
    } catch {
      /* apiPut muestra growl */
    } finally {
      setReorderingMedia(false);
    }
  };

  const applyMediaDragReorder = (dragId: string, dropId: string) => {
    if (dragId === dropId || reorderingMedia) return;
    const ids = reorderMediaIds(
      mediaList.map((m) => m.id),
      dragId,
      dropId
    );
    const byId = new Map(mediaList.map((m) => [m.id, m]));
    const reordered = ids.map((id) => byId.get(id)).filter((x): x is ResourceMediaItem => !!x);
    if (reordered.length === mediaList.length) {
      setMediaList(reordered);
    }
    void saveMediaOrder(ids, { silent: true });
  };

  const removeMedia = async (attachmentId: string) => {
    if (!editingId || !agentId || !token) return;
    try {
      await apiDelete(
        `/api/agents/${agentId}/calendar/resources/${editingId}/media/${attachmentId}`,
        token,
        { successMessage: 'Adjunto eliminado' }
      );
      setMediaList((prev) => prev.filter((x) => x.id !== attachmentId));
      await loadResources();
    } catch {
      /* apiDelete ya muestra growl */
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) {
      errors.name = 'El nombre es requerido';
    }
    if (!form.resourceTypeId) {
      errors.resourceTypeId = 'Debe seleccionar un tipo de recurso';
    }
    if (form.capacity < 1) {
      errors.capacity = 'La capacidad debe ser al menos 1';
    }
    if (!RESOURCE_COLOR_RE.test(form.color.trim())) {
      errors.color = 'El color debe ser un hex #RRGGBB';
    }
    // Validate custom attributes keys
    for (let i = 0; i < form.customAttributes.length; i++) {
      if (!form.customAttributes[i].key.trim()) {
        errors[`attr_key_${i}`] = 'La clave no puede estar vacía';
      }
    }
    if (form.icalSyncEnabled && !form.icalImportUrl.trim()) {
      errors.ical = 'La URL iCal es obligatoria cuando el sync está activo';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    try {
      // Convert custom attributes array to object
      const customAttributes: Record<string, string> = {};
      for (const attr of form.customAttributes) {
        if (attr.key.trim()) {
          customAttributes[attr.key.trim()] = attr.value;
        }
      }
      const payload = {
        name: form.name.trim(),
        resourceTypeId: form.resourceTypeId,
        description: form.description.trim() || null,
        color: form.color.trim().toUpperCase(),
        capacity: form.capacity,
        isActive: form.isActive,
        customAttributes: Object.keys(customAttributes).length > 0 ? customAttributes : null,
        icalSyncEnabled: form.icalSyncEnabled,
        icalImportUrl: form.icalImportUrl.trim() || null,
      };

      if (editingId) {
        await apiPut(
          `/api/agents/${agentId}/calendar/resources/${editingId}`,
          payload,
          token,
          { successMessage: 'Recurso actualizado' }
        );
        closeModal();
      } else {
        const created = await apiPost<CalendarResource>(
          `/api/agents/${agentId}/calendar/resources`,
          payload,
          token,
          { successMessage: 'Recurso creado' }
        );
        setEditingId(created.id);
        setMediaList(parseResourceMedia(created.mediaAttachments));
      }
      await loadResources();
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleIcalSync = async (resourceId: string) => {
    if (!agentId || !token) return;
    setSyncingResourceId(resourceId);
    setSyncingKind('ical');
    try {
      await apiPost(
        `/api/agents/${agentId}/calendar/resources/${resourceId}/ical/sync`,
        {},
        token,
        { successMessage: 'Sincronización iCal completada' }
      );
      await loadResources();
    } catch (e) {
      showGrowl(e instanceof Error ? e.message : 'Error al sincronizar iCal', 'error');
    } finally {
      setSyncingResourceId(null);
      setSyncingKind(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !agentId || !token) return;
    setDeleting(true);
    try {
      await apiDelete(
        `/api/agents/${agentId}/calendar/resources/${deleteId}`,
        token,
        { successMessage: 'Recurso eliminado' }
      );
      setDeleteId(null);
      await loadResources();
    } catch (e) {
      setError((e as Error).message);
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Custom Attributes Helpers ───────────────────────────────────────────

  const addAttribute = () => {
    setForm({
      ...form,
      customAttributes: [...form.customAttributes, { key: '', value: '' }],
    });
  };

  const updateAttribute = (index: number, field: 'key' | 'value', value: string) => {
    const attrs = [...form.customAttributes];
    attrs[index] = { ...attrs[index], [field]: value };
    setForm({ ...form, customAttributes: attrs });
  };

  const removeAttribute = (index: number) => {
    const attrs = [...form.customAttributes];
    attrs.splice(index, 1);
    setForm({ ...form, customAttributes: attrs });
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function countAttributes(resource: CalendarResource): number {
    if (!resource.customAttributes) return 0;
    return Object.keys(resource.customAttributes).length;
  }

  // ─── Auto-Generation Handlers ────────────────────────────────────────────

  const handleGenerated = (resources: ExtractedResource[]) => {
    setGeneratedResources(resources);
    setPreviewOpen(true);
  };

  const handleGenerateError = (message: string) => {
    setError(message);
  };

  const handleResourcesApplied = (created: number, updated: number) => {
    setPreviewOpen(false);
    setGeneratedResources([]);
    const parts: string[] = [];
    if (created > 0) {
      parts.push(`${created} creado${created === 1 ? '' : 's'}`);
    }
    if (updated > 0) {
      parts.push(`${updated} actualizado${updated === 1 ? '' : 's'}`);
    }
    showGrowl(parts.join(', ') || 'Cambios aplicados', 'success');
    void loadResources();
    void loadResourceTypes();
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/calendar/resource-types`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a tipos de recurso
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconBox />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Recursos
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Gestiona los recursos reservables del agente
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {agentId && (
              <AutoGenerateButton
                agentId={agentId}
                hasContext={hasContext}
                onGenerated={handleGenerated}
                onError={handleGenerateError}
              />
            )}
            <Button variant="primary" size="md" onClick={openCreateModal}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconPlus /> Crear Recurso
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: tokens.colors.neutral[400] }}>
            <IconSearch />
          </span>
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 32px',
              fontSize: 14,
              borderRadius: tokens.borderRadius.md,
              border: `1px solid ${tokens.colors.neutral[300]}`,
              backgroundColor: tokens.colors.neutral[50],
              color: tokens.colors.neutral[900],
              outline: 'none',
            }}
          />
        </div>
        <select
          value={filterTypeId}
          onChange={(e) => setFilterTypeId(e.target.value)}
          style={{
            padding: '8px 12px',
            fontSize: 14,
            borderRadius: tokens.borderRadius.md,
            border: `1px solid ${tokens.colors.neutral[300]}`,
            backgroundColor: tokens.colors.neutral[50],
            color: tokens.colors.neutral[900],
            outline: 'none',
            minWidth: 160,
          }}
        >
          <option value="">Todos los tipos</option>
          {resourceTypes.map((rt) => (
            <option key={rt.id} value={rt.id}>{rt.name}</option>
          ))}
        </select>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          style={{
            padding: '8px 12px',
            fontSize: 14,
            borderRadius: tokens.borderRadius.md,
            border: `1px solid ${tokens.colors.neutral[300]}`,
            backgroundColor: tokens.colors.neutral[50],
            color: tokens.colors.neutral[900],
            outline: 'none',
            minWidth: 120,
          }}
        >
          <option value="">Todos</option>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando recursos...
        </div>
      )}

      {/* Empty state */}
      {!loading && resources.length === 0 && !error && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500], padding: 24 }}>
            <IconBox />
            <p style={{ marginTop: 12, marginBottom: 16 }}>No hay recursos configurados.</p>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              Crear primer recurso
            </Button>
          </div>
        </Card>
      )}

      {/* Resources List */}
      {!loading && resources.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {resources.map((resource) => {
            const mediaCount = parseResourceMedia(resource.mediaAttachments).length;
            return (
            <Card key={resource.id} variant="outlined" padding="md">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span
                      aria-hidden
                      title={resource.color || undefined}
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 4,
                        flexShrink: 0,
                        background: resource.color && RESOURCE_COLOR_RE.test(resource.color)
                          ? resource.color
                          : tokens.colors.neutral[300],
                        border: `1px solid ${tokens.semantic.border}`,
                      }}
                    />
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
                      {resource.name}
                    </h3>
                    <Badge variant={resource.isActive ? 'success' : 'warning'} size="sm">
                      {resource.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: tokens.colors.neutral[500], flexWrap: 'wrap' }}>
                    <span>Tipo: {resource.resourceType?.name || '—'}</span>
                    <span>Capacidad: {resource.capacity}</span>
                    {countAttributes(resource) > 0 && (
                      <span>Atributos: {countAttributes(resource)}</span>
                    )}
                    {mediaCount > 0 && <span>Medios: {mediaCount}</span>}
                    {resource.icalSyncEnabled && resource.icalImportUrl ?
                      <span style={{ color: resource.icalLastSyncError ? tokens.colors.error.dark : undefined }}>
                        iCal
                        {resource.icalLastSyncedAt ?
                          ` · sync ${new Date(resource.icalLastSyncedAt).toLocaleString('es')}`
                        : ''}
                        {resource.icalLastSyncError ? ` · error` : ''}
                      </span>
                    : null}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {resource.icalSyncEnabled && resource.icalImportUrl ?
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={syncingResourceId === resource.id && syncingKind === 'ical'}
                      disabled={syncingResourceId !== null}
                      onClick={() => void handleIcalSync(resource.id)}
                    >
                      Sync iCal
                    </Button>
                  : null}
                  <Link
                    to={`/app/agents/${agentId}/calendar/resources/${resource.id}/availability`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.colors.primary[600],
                      textDecoration: 'none',
                      borderRadius: tokens.borderRadius.md,
                      border: `1px solid ${tokens.colors.primary[200]}`,
                      backgroundColor: tokens.colors.primary[50],
                      transition: `background-color ${tokens.transitions.fast}`,
                    }}
                    title="Configurar disponibilidad"
                  >
                    📅 Disponibilidad
                  </Link>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(resource)}>
                    <IconEdit />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(resource.id)}>
                    <IconTrash />
                  </Button>
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Editar Recurso' : 'Crear Recurso'}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Name */}
          <Input
            label="Nombre"
            placeholder="Ej: Cabaña Norte, Consultorio 1, Salón Principal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={formErrors.name}
            fullWidth
          />

          {/* Resource Type */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Tipo de Recurso
            </label>
            <select
              value={form.resourceTypeId}
              onChange={(e) => setForm({ ...form, resourceTypeId: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${formErrors.resourceTypeId ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              <option value="">Seleccionar tipo...</option>
              {resourceTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>{rt.name}</option>
              ))}
            </select>
            {formErrors.resourceTypeId && (
              <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>
                {formErrors.resourceTypeId}
              </div>
            )}
          </div>

          {/* Color */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Color en calendario
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={RESOURCE_COLOR_RE.test(form.color) ? form.color : '#3B82F6'}
                onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })}
                style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                aria-label="Selector de color del recurso"
              />
              <input
                value={form.color}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v === '' || /^#[0-9a-fA-F]{0,6}$/.test(v)) {
                    setForm({ ...form, color: v.toUpperCase() });
                  }
                }}
                placeholder="#3B82F6"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${formErrors.color ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                  outline: 'none',
                  fontFamily: 'ui-monospace, monospace',
                }}
              />
            </div>
            {formErrors.color ?
              <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>
                {formErrors.color}
              </div>
            : (
              <div style={{ fontSize: 12, color: tokens.colors.neutral[500], marginTop: 4 }}>
                Se usa en los chips del calendario PWA.
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Descripción (opcional)
            </label>
            <textarea
              placeholder="Descripción del recurso..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Capacity */}
          <Input
            label="Capacidad"
            type="number"
            min={1}
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })}
            error={formErrors.capacity}
            helperText="Número máximo de reservas simultáneas"
            fullWidth
          />

          {/* Is Active */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="isActive"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="isActive" style={{ fontSize: 14, color: tokens.colors.neutral[700], cursor: 'pointer' }}>
              Recurso activo
            </label>
          </div>

          <div
            style={{
              border: `1px solid ${tokens.colors.neutral[200]}`,
              borderRadius: tokens.borderRadius.md,
              padding: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: tokens.colors.neutral[800], marginBottom: 8 }}>
              Airbnb iCal
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: tokens.colors.neutral[500] }}>
              Pegá la URL de exportación del calendario de Airbnb para importar bloqueos de disponibilidad.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="icalSyncEnabled"
                checked={form.icalSyncEnabled}
                onChange={(e) => setForm({ ...form, icalSyncEnabled: e.target.checked })}
              />
              <label htmlFor="icalSyncEnabled" style={{ fontSize: 14, cursor: 'pointer' }}>
                Sincronizar disponibilidad desde iCal
              </label>
            </div>
            <Input
              label="URL iCal"
              value={form.icalImportUrl}
              onChange={(e) => setForm({ ...form, icalImportUrl: e.target.value })}
              placeholder="https://www.airbnb.com/calendar/ical/....ics?s=..."
            />
            {editingId && resources.find((r) => r.id === editingId) ?
              <div style={{ marginTop: 8, fontSize: 12, color: tokens.colors.neutral[500] }}>
                {resources.find((r) => r.id === editingId)?.icalLastSyncedAt ?
                  `Último sync: ${new Date(resources.find((r) => r.id === editingId)!.icalLastSyncedAt!).toLocaleString('es')}`
                : 'Sin sync exitoso aún'}
                {resources.find((r) => r.id === editingId)?.icalLastSyncError ?
                  <span style={{ color: tokens.colors.error.dark, display: 'block', marginTop: 4 }}>
                    {resources.find((r) => r.id === editingId)!.icalLastSyncError}
                  </span>
                : null}
              </div>
            : null}
            {formErrors.ical ?
              <p style={{ margin: '8px 0 0', fontSize: 13, color: tokens.colors.error.dark }}>{formErrors.ical}</p>
            : null}
          </div>

          {/* Fotos y videos */}
          <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
            <label style={{ fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], display: 'block', marginBottom: 4 }}>
              Fotos y videos
            </label>
            {editingId && mediaList.length > 1 ? (
              <p style={{ margin: '0 0 8px', fontSize: 12, color: tokens.colors.neutral[500] }}>
                El orden importa: el bot envía las imágenes y videos al visitante en este mismo orden (arrastrá para reordenar).
              </p>
            ) : null}
            {!editingId ? (
              <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[500] }}>
                Guardá el recurso con &quot;Crear&quot; para poder adjuntar archivos (arrastrar o elegir desde el equipo).
              </p>
            ) : (
              <>
                <input
                  ref={mediaInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const fl = e.target.files;
                    if (fl?.length) void processMediaFiles(fl);
                    e.target.value = '';
                  }}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!uploadProgress) mediaInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!uploadProgress) setDragOverMedia(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverMedia(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverMedia(false);
                    if (uploadProgress) return;
                    const fl = e.dataTransfer.files;
                    if (fl?.length) void processMediaFiles(fl);
                  }}
                  onClick={() => {
                    if (!uploadProgress) mediaInputRef.current?.click();
                  }}
                  style={{
                    border: `2px dashed ${dragOverMedia ? tokens.colors.primary[400] : tokens.colors.neutral[300]}`,
                    borderRadius: tokens.borderRadius.md,
                    padding: 20,
                    textAlign: 'center',
                    cursor: uploadProgress ? 'wait' : 'pointer',
                    backgroundColor: dragOverMedia ? tokens.colors.primary[50] : tokens.colors.neutral[50],
                    opacity: uploadProgress ? 0.75 : 1,
                  }}
                >
                  <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[600] }}>
                    Arrastrá imágenes o videos aquí, o hacé clic para elegir varios archivos
                  </p>
                </div>
                {uploadProgress && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: tokens.colors.neutral[600], marginBottom: 4 }}>
                      Subiendo {uploadProgress.fileName}… {uploadProgress.percent}%
                    </div>
                    <div
                      style={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: tokens.colors.neutral[200],
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${uploadProgress.percent}%`,
                          backgroundColor: tokens.colors.primary[500],
                          transition: 'width 0.12s ease-out',
                        }}
                      />
                    </div>
                  </div>
                )}
                {mediaList.length > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {mediaList.map((m, index) => {
                      const url = mediaBlobUrls[m.id];
                      const isImage = m.kind === 'image' || m.mimeType.startsWith('image/');
                      const isVideo = m.kind === 'video' || m.mimeType.startsWith('video/');
                      const previewLoading =
                        !url && !mediaPreviewErrorIds.has(m.id) && (isImage || isVideo);
                      const canDrag = mediaList.length > 1 && !reorderingMedia;
                      const isDragging = mediaDragId === m.id;
                      const isDropTarget = mediaDropTargetId === m.id && mediaDragId !== m.id;
                      return (
                        <div
                          key={m.id}
                          onDragOver={(e) => {
                            if (!canDrag || !mediaDragId || mediaDragId === m.id) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setMediaDropTargetId(m.id);
                          }}
                          onDragLeave={(e) => {
                            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                            setMediaDropTargetId((prev) => (prev === m.id ? null : prev));
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const dragId = e.dataTransfer.getData('text/plain') || mediaDragId;
                            if (dragId) applyMediaDragReorder(dragId, m.id);
                            setMediaDragId(null);
                            setMediaDropTargetId(null);
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            padding: 8,
                            borderRadius: tokens.borderRadius.md,
                            border: `1px solid ${isDropTarget ? tokens.colors.primary[400] : tokens.colors.neutral[200]}`,
                            backgroundColor: isDropTarget ? tokens.colors.primary[50] : tokens.colors.neutral[50],
                            opacity: isDragging ? 0.45 : 1,
                            boxShadow: isDropTarget ? `0 0 0 2px ${tokens.colors.primary[200]}` : undefined,
                            transition: `background-color ${tokens.transitions.fast}, opacity ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}`,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 4,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: tokens.colors.neutral[600],
                                minWidth: 18,
                              }}
                            >
                              {index + 1}
                            </span>
                            <button
                              type="button"
                              draggable={canDrag}
                              disabled={!canDrag}
                              aria-label="Arrastrar para reordenar"
                              title="Arrastrar para reordenar"
                              onDragStart={(e) => {
                                setMediaDragId(m.id);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', m.id);
                              }}
                              onDragEnd={() => {
                                setMediaDragId(null);
                                setMediaDropTargetId(null);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 24,
                                padding: 0,
                                border: `1px solid ${tokens.colors.neutral[300]}`,
                                borderRadius: tokens.borderRadius.sm,
                                backgroundColor: '#fff',
                                color: tokens.colors.neutral[500],
                                cursor: canDrag ? 'grab' : 'not-allowed',
                                touchAction: 'none',
                              }}
                            >
                              <IconGrip />
                            </button>
                          </div>
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              aspectRatio: '1',
                              borderRadius: tokens.borderRadius.md,
                              overflow: 'hidden',
                              border: `1px solid ${tokens.colors.neutral[200]}`,
                              backgroundColor: tokens.colors.neutral[100],
                            }}
                            onMouseEnter={(e) => {
                              if (url && isImage) openMediaZoom(m.id, e.currentTarget);
                            }}
                            onMouseLeave={scheduleMediaZoomClose}
                          >
                            {url && isImage ? (
                              <img
                                src={url}
                                alt={m.originalName || 'Adjunto'}
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  cursor: 'zoom-in',
                                  display: 'block',
                                  transition: `transform ${tokens.transitions.fast}`,
                                  transform: mediaZoomAnchor?.id === m.id ? 'scale(1.06)' : 'scale(1)',
                                }}
                              />
                            ) : url && isVideo ? (
                              <video
                                src={url}
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                                muted
                                playsInline
                                controls
                              />
                            ) : previewLoading ? (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  color: tokens.colors.neutral[500],
                                }}
                              >
                                Cargando…
                              </div>
                            ) : (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 4,
                                  fontSize: 10,
                                  padding: 6,
                                  wordBreak: 'break-word',
                                  textAlign: 'center',
                                  color: mediaPreviewErrorIds.has(m.id)
                                    ? tokens.colors.error.dark
                                    : tokens.colors.neutral[600],
                                }}
                              >
                                {mediaPreviewErrorIds.has(m.id) ? (
                                  <>
                                    <span>No se pudo cargar</span>
                                    <span style={{ fontSize: 9, color: tokens.colors.neutral[500] }}>
                                      Archivo ausente — volvé a subir
                                    </span>
                                  </>
                                ) : (
                                  m.originalName || m.mimeType || 'Sin vista previa'
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              title="Quitar adjunto"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                void removeMedia(m.id);
                              }}
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 24,
                                height: 24,
                                padding: 0,
                                lineHeight: '22px',
                                fontSize: 16,
                                border: 'none',
                                borderRadius: tokens.borderRadius.sm,
                                cursor: 'pointer',
                                backgroundColor: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Custom Attributes */}
          <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700] }}>
                Atributos Personalizados
              </label>
              <Button variant="ghost" size="sm" onClick={addAttribute}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconPlus /> Agregar</span>
              </Button>
            </div>
            {form.customAttributes.length === 0 && (
              <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[400] }}>
                Sin atributos personalizados. Haz clic en "Agregar" para añadir pares clave-valor.
              </p>
            )}
            {form.customAttributes.map((attr, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Clave"
                  value={attr.key}
                  onChange={(e) => updateAttribute(i, 'key', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    fontSize: 13,
                    borderRadius: tokens.borderRadius.sm,
                    border: `1px solid ${formErrors[`attr_key_${i}`] ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                  }}
                />
                <input
                  type="text"
                  placeholder="Valor"
                  value={attr.value}
                  onChange={(e) => updateAttribute(i, 'value', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    fontSize: 13,
                    borderRadius: tokens.borderRadius.sm,
                    border: `1px solid ${tokens.colors.neutral[300]}`,
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeAttribute(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: tokens.colors.error.main, padding: 4 }}
                  aria-label="Eliminar atributo"
                >
                  <IconTrash />
                </button>
              </div>
            ))}
            {Object.keys(formErrors).some((k) => k.startsWith('attr_key_')) && (
              <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginTop: 4 }}>
                Las claves de atributos no pueden estar vacías
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Eliminar Recurso"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas eliminar este recurso? Esta acción no se puede deshacer y eliminará todas las reglas de disponibilidad y reservas asociadas.
        </p>
      </Modal>

      {/* Auto-Generation Preview Modal */}
      {agentId && (
        <GenerationPreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          resources={generatedResources}
          existingResources={resources.map((r) => ({
            id: r.id,
            name: r.name,
            description: r.description,
            capacity: r.capacity,
            resourceTypeId: r.resourceTypeId,
            customAttributes: r.customAttributes,
          }))}
          resourceTypes={resourceTypes}
          agentId={agentId}
          onApplied={handleResourcesApplied}
        />
      )}

      {modalOpen &&
      mediaZoomAnchor &&
      (() => {
        const zoomItem = mediaList.find((x) => x.id === mediaZoomAnchor.id);
        const zoomUrl = zoomItem ? mediaBlobUrls[zoomItem.id] : undefined;
        if (!zoomItem || !zoomUrl || zoomItem.kind !== 'image') return null;
        return (
          <ResourceMediaZoomPopover
            anchor={mediaZoomAnchor}
            url={zoomUrl}
            label={zoomItem.originalName || ''}
            onKeepOpen={cancelMediaZoomClose}
            onScheduleClose={scheduleMediaZoomClose}
          />
        );
      })()}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes mediaZoomIn {
          from {
            opacity: 0;
            transform: scale(0.96);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default CalendarResourcesPage;
